import { toast } from 'react-toastify';
import { apiSlice } from 'api/apiSlice';
import http from './http';
import { toCamelCase, getErrorMessage, getVerificationConfiguration } from 'utils/utilities';
import { endpoints } from 'helper/endpoints';
import {
    setProfileData,
    setProfileLoading,
    setProfileError,
    setTestResult,
    setTestResultLoading,
    clearTestResultLoading,
} from 'store/common/slices/profile';
import { setDefaultRoute } from 'store/common/slices/navigation';
import { setVerificationConfiguration } from 'store/common/slices/general';
import { setTwoFaSession } from 'features/preIdentity/state/slice';
import { setDocumentAttempt } from 'features/govtId/state/slice';
import { updateDefaultRoute } from 'lib/navigation';

// ===============RTK API DEFINITIONS START ==============

const profileApis = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getProfile: builder.query({
            query: () => ({
                url: endpoints.getProfile,
                method: 'GET',
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    dispatch(setProfileLoading(true));

                    const { data } = await queryFulfilled;

                    if (data) {
                        // update defaultRoute BEFORE profile data: Home's
                        // consent-redirect effect can flush between these
                        // dispatches, and it navigates via defaultRoute — if the
                        // profile lands first the redirect uses the previous
                        // session's (or an empty) route list and skips 2FA /
                        // risk-assessment.
                        const { integrationSettings, ...restData } = data;

                        const routePayload = {
                            integrationSettings,
                            ...toCamelCase(restData),
                        };

                        dispatch(setDefaultRoute(updateDefaultRoute(routePayload)));

                        // update profile state
                        dispatch(setProfileData(data));

                        // derive the verification mode (SINGLE / AND / OR)
                        const verificationConfiguration = getVerificationConfiguration(
                            data?.workflow?.id_verifications
                        );

                        if (verificationConfiguration) {
                            dispatch(setVerificationConfiguration(verificationConfiguration));
                        }

                        // seed govt-ID document-attempt count/uuid from the server
                        // response (count is incremented locally on each capture).
                        const user = data?.company?.user ?? {};
                        dispatch(
                            setDocumentAttempt({
                                count: user?.verificationDetails?.govt_id_attempts,
                                uuid: user?.userDetails?.uuid,
                            })
                        );

                        // sync twoFaSession into preIdentity slice
                        const twoFaSession = data?.company?.user?.twoFaSession;
                        if (twoFaSession) dispatch(setTwoFaSession(twoFaSession));
                    }
                } catch (err) {
                    /*
                     * An aborted query is not a failure and must not be reported as one. The last
                     * subscriber unmounting mid-flight rejects `queryFulfilled` with a raw
                     * `AbortError` that carries no response, so the message fell through to the
                     * generic default — and this one does not merely show a toast: it sends a user
                     * with a perfectly good session to `/session-expired`. `ConditionError` is the
                     * same story for a query that never ran.
                     *
                     * The `finally` below still runs on the way out, so the loading flag comes down
                     * either way.
                     */
                    const name = err?.error?.name;

                    if (name === 'AbortError' || name === 'ConditionError') return;

                    // Surface link expired/invalid (and other fetch failures) so the
                    // consent screen can route to /session-expired.
                    dispatch(
                        setProfileError(getErrorMessage({ response: { data: err?.error?.data } }))
                    );
                } finally {
                    dispatch(setProfileLoading(false));
                }
            },
        }),

        getTestResult: builder.query({
            query: (userAccessCode) => ({
                url: endpoints.getTestResult,
                method: 'POST',
                data: { userAccessCode },
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                dispatch(setTestResultLoading());
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setTestResult(data));
                } catch (err) {
                    /**
                     * `queryFulfilled` rejects for two quite different reasons, and only one of them
                     * is a failure.
                     *
                     * A query that is *aborted* rejects here too — which is what the last subscriber
                     * unmounting does, and this endpoint is triggered from screens that close on
                     * success, so it happens on the ordinary path. The rejection carries the raw
                     * `AbortError` rather than anything the base query reported, so there is no
                     * response on it and the message fell all the way through to the generic default:
                     * "Something went wrong." over a request the network tab shows completing
                     * normally. `ConditionError` is the same story for a query that never ran.
                     *
                     * Nothing is recorded against the result for those — the request did not answer,
                     * which is not the same as answering badly — but the loading flag still has to
                     * come down, because screens wait on it.
                     */
                    const name = err?.error?.name;

                    if (name === 'AbortError' || name === 'ConditionError') {
                        dispatch(clearTestResultLoading());
                        return;
                    }

                    const message = getErrorMessage({ response: { data: err?.error?.data } });
                    dispatch(setTestResult({ error: message }));
                    toast.error(message);
                }
            },
        }),

        setConsent: builder.mutation({
            query: ({ userAccessCode, name }) => ({
                url: endpoints.setConsent,
                method: 'POST',
                data: {
                    userAccessCode,
                    isBackgroundDisclosureAccepted: 1,
                    GLBPurposeAndDPPAPurpose: 1,
                    FCRAPurpose: 1,
                    fullName: name,
                },
            }),
        }),

        updateUserProfile: builder.mutation({
            query: (data) => ({
                url: endpoints.updateUserProfile,
                method: 'PUT',
                data: toCamelCase(data),
            }),
        }),

        setProgress: builder.mutation({
            query: (status) => ({
                url: endpoints.verificationProgress,
                method: 'POST',
                data: { status },
            }),
        }),
    }),
    overrideExisting: true,
});

export const {
    useGetProfileQuery,
    useLazyGetProfileQuery,
    useLazyGetTestResultQuery,
    useUpdateUserProfileMutation,
    useSetConsentMutation,
    useSetProgressMutation,
} = profileApis;

// Exposed so non-hook callers (connect() containers, thunks) can trigger the
// query via `profileApis.endpoints.getProfile.initiate(...)`.
export { profileApis };

// =============== RTK API DEFINITIONS+ END =================

export const resumeWorkflowApi = (userAccessCode) => {
    return http.post('/user/medallion/resume-workflow', {
        userAccessCode,
    });
};

// Progress poll backing the redux-polling loop (drives the device-links status
// check). Plain axios because the polling middleware awaits its return value
// directly and reads `.data` off it.
export const getStatusAPI = () => http.get(endpoints.verificationProgress);
